import * as React from 'react'
import { useQueryState } from 'nuqs'
import type { ColumnDef, Row, RowSelectionState } from '@tanstack/react-table'
import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { useCapability } from '@/features/permissions/hooks'
import { useCallsQuery } from '@/hooks/useCallsQuery'
import { useCallsCountQuery } from '@/hooks/useCallsCountQuery'
import { useCallDutyOptions } from '@/hooks/useCallDutyOptions'
import {
  type CallRubricOption,
  useCallRubricOptions,
} from '@/hooks/useCallRubricOptions'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { AlertCircle } from 'lucide-react'
import type { CallRecord } from '../types'
import { createCallsFilterFields, callsSchema } from './callsTableSchema'
import { callsColumns } from './CallLogsColumns'
import { CallLogsSheetWrapper } from './CallLogsSheetDetails'
import { CallLogsCopyFilterButton } from './CallLogsCopyFilterButton'
import { columnFiltersToFilterConditions } from '../utils/mergeFilters'
import { callsContainsQueryTransforms } from '../utils/phoneFilterQuery'
import {
  createRubricFilterColumns,
  hasRubricFilterParam,
  isRubricFilterId,
  rubricFilterConditions,
  rubricFilterSchemaFields,
} from '../rubricFilters'

const TABLE_ID = 'call-logs'
const LEGACY_FILTER_FIELD_IDS = ['duty_name']

/**
 * Sheet slot wrapper: renders the call detail sheet and keeps the `details` URL
 * flag in sync with the sheet's open state — set `details=1` while a row is
 * selected, drop it when selection clears, so the flag never lingers after close.
 *
 * The sync lives here (not in CallLogsInner) because it reads `rowSelection`,
 * which only exists in the DataTable context created inside DataTableInfinite —
 * a context CallLogsInner sits above. This sheet slot renders within it.
 */
function CallLogsSheet() {
  const { rowSelection } = useDataTable<CallRecord, unknown>()
  const [detailsParam, setDetailsParam] = useQueryState('details', {
    history: 'replace',
  })

  const detailsDesired = Object.keys(rowSelection).length > 0 ? '1' : null
  React.useEffect(() => {
    if (detailsParam !== detailsDesired) void setDetailsParam(detailsDesired)
  }, [detailsDesired, detailsParam, setDetailsParam])

  return <CallLogsSheetWrapper />
}

// Inner component runs inside DataTableStoreProvider so it can read filter state
function CallLogsInner({
  filterFields,
  rubrics,
}: {
  filterFields: ReturnType<typeof createCallsFilterFields>
  rubrics: CallRubricOption[]
}) {
  // Read the OpenStatus filter state from the BYOS adapter (set by DataTableStoreSync)
  const storeState = useFilterState()
  const { resetAllFilters } = useFilterActions()
  const showCopyFilter = useCapability('calls.copyFilter') === true

  // Compute the initial row selection ONCE: open the detail sheet when the URL
  // carries `?call_id=<id>&details=1`. Rows are keyed by call_id (see getRowId).
  const [detailsParam] = useQueryState('details')
  const [initialRowSelection] = React.useState<RowSelectionState>(() => {
    const callId =
      typeof storeState?.call_id === 'string' ? storeState.call_id : null
    return detailsParam === '1' && callId ? { [callId]: true } : {}
  })

  // Derive API filter params from OpenStatus store state whenever it changes
  const apiFilters = React.useMemo(() => {
    // Only include keys that are actual filter fields — exclude store metadata
    // like `uuid` (selected row ID) and `sort` which DataTableStoreSync also
    // writes into the adapter. Without this guard, selecting a row changes
    // storeState → changes apiFilters → changes the React Query key,
    // triggering a spurious refetch that empties the table.
    const filterFieldIds = new Set([
      ...filterFields.map((f) => String(f.value)),
      ...LEGACY_FILTER_FIELD_IDS,
    ])
    const columnFilters = Object.entries(storeState ?? {})
      .filter(([key, value]) => {
        if (!filterFieldIds.has(key) || isRubricFilterId(key)) return false
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        return String(value).trim().length > 0
      })
      .map(([id, value]) => ({ id, value }))

    return [
      ...columnFiltersToFilterConditions(
        columnFilters,
        callsContainsQueryTransforms
      ),
      // Rubric filters carry the rubric's type with the picked values, as
      // relay reads them (see rubricFilters.ts).
      ...rubricFilterConditions(rubrics, storeState ?? {}),
    ]
  }, [filterFields, rubrics, storeState])

  const callsQuery = useCallsQuery({
    filters: apiFilters.length > 0 ? apiFilters : undefined,
    limit: 50,
    sort_by: 'created_at',
    sort_order: 'desc',
  })

  const { isFetching, dataUpdatedAt, refetch } = callsQuery

  const data = React.useMemo(
    () => callsQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [callsQuery.data]
  )

  // Total rows matching the current filters, fetched decoupled from the rows so
  // the table is never blocked on the count. The footer shows "<loaded> of <total>".
  const countQuery = useCallsCountQuery({
    filters: apiFilters.length > 0 ? apiFilters : undefined,
  })
  const totalRows = countQuery.data?.count

  const columns = React.useMemo<ColumnDef<CallRecord>[]>(
    () => [...callsColumns('duty_id'), ...createRubricFilterColumns(rubrics)],
    [rubrics]
  )

  const getRowId = React.useCallback((row: CallRecord) => row.call_id, [])

  // Tint test-call rows with the brand accent purple (paired with a flask icon
  // in the Type cell) so they read as non-production at a glance.
  const getRowClassName = React.useCallback(
    (row: Row<CallRecord>) =>
      row.original.test_call
        ? 'bg-accent-subtle hover:bg-[var(--purple-4)]'
        : '',
    []
  )

  // Wrapper component (not a JSX expression) so it can call useDataTable from
  // inside the DataTableProvider that DataTableInfinite mounts — CallLogsSheet
  // syncs the `details` URL flag and renders the v3 detail sheet.
  const SheetSlot = React.useMemo(() => <CallLogsSheet />, [])

  const ToolbarActions = React.useMemo(
    () => (
      <>
        {showCopyFilter && apiFilters.length > 0 && (
          <CallLogsCopyFilterButton />
        )}
        <TabRefreshButton onRefresh={refetch} isFetching={isFetching} />
      </>
    ),
    [isFetching, refetch, showCopyFilter, apiFilters.length]
  )

  return (
    <DataTableInfinite<CallRecord, unknown>
      tableId={TABLE_ID}
      columns={columns}
      data={data}
      filterFields={filterFields}
      manualFiltering
      manualSorting
      isLoading={callsQuery.isLoading}
      isFetching={isFetching}
      isError={callsQuery.isError}
      error={callsQuery.error}
      entityLabel="calls"
      onClearFilters={resetAllFilters}
      hasNextPage={callsQuery.hasNextPage}
      fetchNextPage={callsQuery.fetchNextPage}
      refetch={refetch}
      totalRows={totalRows}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      getRowClassName={getRowClassName}
      defaultRowSelection={initialRowSelection}
      dataUpdatedAt={dataUpdatedAt}
      sheetSlot={SheetSlot}
      toolbarActions={ToolbarActions}
      autoColumnWidth
    />
  )
}

/**
 * Call Logs — OpenStatus data-table view.
 *
 * Filters come from the OpenStatus sidebar (Cmd+B) — table column filters →
 * BYOS URL (nuqs) adapter.
 *
 * All filters are converted to FilterCondition[] and passed to useCallsQuery.
 * The nuqs adapter persists those filters in the URL, so a filtered view is
 * shareable and a `?call_id=<id>&details=1` link deep-links to a call.
 */
function ConfiguredCallLogs() {
  const dutyOptionsQuery = useCallDutyOptions()
  const dutyOptionsState =
    dutyOptionsQuery.isFetching && !dutyOptionsQuery.data
      ? 'loading'
      : dutyOptionsQuery.isError
        ? 'error'
        : 'ready'
  const { refetch: refetchDutyOptions } = dutyOptionsQuery
  const retryDutyOptions = React.useCallback(() => {
    void refetchDutyOptions()
  }, [refetchDutyOptions])
  // One filter per rubric. The server answers an empty list to viewers who
  // may not read rubric results, so this waits on one request, not on the
  // capability and rollout lookups too.
  const rubricOptionsQuery = useCallRubricOptions()
  const rubrics = React.useMemo(
    () => rubricOptionsQuery.data ?? [],
    [rubricOptionsQuery.data]
  )
  const filterFields = React.useMemo(
    () =>
      createCallsFilterFields(
        dutyOptionsQuery.data ?? [],
        { state: dutyOptionsState, onRetry: retryDutyOptions },
        rubrics
      ),
    [dutyOptionsQuery.data, dutyOptionsState, retryDutyOptions, rubrics]
  )
  // The table seeds its filters from the URL once, on mount. So a link
  // carrying rubric filters waits for the rubric list, or they'd be dropped.
  // Any other visit renders at once, and the rubric filters join the sidebar
  // and the URL store when the list lands, without a remount. Picks a rubric
  // no longer offers (after a save) are never sent (rubricFilterConditions).
  const [linkHasRubricFilter] = React.useState(() =>
    hasRubricFilterParam(window.location.search)
  )
  if (rubricOptionsQuery.isLoading && linkHasRubricFilter) {
    return <CallLogsLoading />
  }

  return (
    <>
      {linkHasRubricFilter && rubricOptionsQuery.isError && (
        <Alert variant="destructive" className="mx-4 mt-3 w-auto">
          <AlertCircle />
          <AlertDescription className="flex items-center gap-3">
            <span>
              Rubric filters couldn’t load, so this list isn’t filtered by
              rubric.
            </span>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => void rubricOptionsQuery.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <CallLogsStore
        // A link's rubric filters are read from the URL on mount, so once a
        // retried list lands it remounts to read them.
        key={linkHasRubricFilter ? JSON.stringify(rubrics) : 'calls'}
        filterFields={filterFields}
        rubrics={rubrics}
      />
    </>
  )
}

function CallLogsLoading() {
  return (
    <div
      className="flex flex-col gap-2 p-4"
      role="status"
      aria-label="Loading calls">
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  )
}

function CallLogsStore({
  filterFields,
  rubrics,
}: {
  filterFields: ReturnType<typeof createCallsFilterFields>
  rubrics: CallRubricOption[]
}) {
  const schema = React.useMemo(
    () => ({ ...callsSchema.definition, ...rubricFilterSchemaFields(rubrics) }),
    [rubrics]
  )
  const adapter = useNuqsAdapter(schema, {
    id: TABLE_ID,
    history: 'replace',
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <CallLogsInner filterFields={filterFields} rubrics={rubrics} />
    </DataTableStoreProvider>
  )
}

export function CallLogs() {
  return <ConfiguredCallLogs />
}
